import { useTranslation } from '@pypestream/translations';
import {
  AppErrorBoundary,
  resolveErrorFlags,
  type ChildrenProps,
} from '@pypestream/ui-patterns';
import { isDevelopment } from '@pypestream/utils';
import { FC } from 'react';

import {
  useCurrentState,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';

/**
 * Pro Studio's root error boundary.
 *
 * Everything that is not app-specific lives in `@pypestream/ui-patterns`; what
 * stays here is exactly what differs between the two apps — the XState hooks
 * this app reads and its own translation scope.
 *
 * The stale-chunk reload is NOT wired in here. `reloadOnceForChunkError` does
 * not inspect the error at all — it reloads unconditionally — so it is only
 * correct where the caller already knows the failure is a chunk load. That
 * caller is the `vite:preloadError` listener in `src/main.tsx`, and it stays
 * the only one.
 */
export const ErrorBoundary: FC<ChildrenProps> = ({ children }) => {
  const [t] = useTranslation();
  const currentState = useCurrentState();
  const errors = useProStudioCtxSelector((ctx) => ctx.errors);

  return (
    <AppErrorBoundary
      errors={errors}
      flags={resolveErrorFlags(Object.values(currentState))}
      isDevelopment={isDevelopment}
      labels={{
        reload: String(t('pro-studio/common:errorBoundary.reloadPage')),
        home: String(t('pro-studio/common:errorBoundary.goHome')),
      }}
    >
      {children}
    </AppErrorBoundary>
  );
};
